import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cable, Plus, Trash2, Edit, Radio, ArrowRight, ArrowLeft, ArrowLeftRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCounters, meterPropsFromTrunk } from "@/lib/counters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChannelMeter, EmptyState, PageHeader, PolicyPill, StatusPill } from "@/components/uiext";
import { CodecPolicyEditor } from "@/components/CodecPolicyEditor";
import { TracesPane } from "@/components/TracesSheet";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { SectionHeading, StatusSquare } from "./_tenantCommon.jsx";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";
import { PRESET_LIST, getPreset, buildUsername, materializeCustomPreset } from "@/lib/trunkPresets";

const EMPTY = {
  name: "",
  // Phase 6o Hotfix H — trunks.provider_id retired (Provider/Reseller
  // tier is gone). Trunks now belong directly to their org via
  // org_id; there's no longer a pointer to a tier-1 provider tenant.
  provider_preset: "generic",
  provider_account: "",
  provider_subaccount: "",
  host: "",
  port: 5060,
  transport: "udp",
  auth_mode: "ip",
  username: "",
  password: "",
  channels: 0,
  channels_in: 0,
  channels_out: 0,
  channel_policy: "total",
  direction: "both",
  codecs: ["PCMU", "PCMA"],
  allowed_ips: [],
  from_user: "elswitch",
  failover_trunk_ids: [],
  use_srv: false,
  register_with_carrier: false,
  register_expiry_seconds: 300,
  enabled: true,
};

function relTime(ts) {
  if (!ts) return null;
  const diff = Math.max(0, Date.now() / 1000 - Number(ts));
  if (diff < 2) return "just now";
  if (diff < 60) return `${Math.round(diff)}s ago`;
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

/**
 * computeReachState — derives visual state from a trunk_health doc.
 * Shared by the LED (in the stacked Trunk cell) and the detail pill
 * (in the Reachability cell) so they can't disagree.
 *
 * Phase 6ab (Hotfix U) — registration takes priority over OPTIONS
 * reachability. Some carriers (notably SIP.us in SRV-pool mode) do
 * not reliably ack OPTIONS pings on the logical pool hostname even
 * though the registration itself is alive and routing inbound calls
 * normally. Treating OPTIONS-miss as the canonical "down" signal
 * caused the LED to flap red every ~90s while the trunk was actually
 * fine. The new precedence is:
 *
 *   register_status===registered  → green (LED reflects the truth: routing works)
 *                                   - if OPTIONS also responding → label = ack code
 *                                   - if OPTIONS silent           → label = "registered (OPTIONS silent)"
 *   register_status===registering → amber pulse
 *   register_status in {auth-failed, rejected, timeout} → red
 *   no registration (IP-auth)     → fall back to OPTIONS-only behaviour
 */
function computeReachState(health) {
  const h = health || {};
  const code = h.last_status_code;
  const rtt = h.last_rtt_ms;
  const reg = h.register_status;
  if (reg === "registered") {
    if (h.reachable === true) {
      // OPTIONS pings are also being acked — happiest state.
      return {
        dotCls: "bg-[hsl(var(--ok))] animate-pulse",
        aria: "ok",
        label: `${code ?? "200"}${typeof rtt === "number" ? ` · ${rtt}ms` : ""}`,
      };
    }
    if (h.reachable === false) {
      // Phase 6ad (Hotfix W) — registered AND OPTIONS keepalives are
      // genuinely going unanswered. Carrier-side quirk; not an outage.
      return {
        dotCls: "bg-[hsl(var(--ok))] animate-pulse",
        aria: "ok",
        label: "registered (OPTIONS silent)",
      };
    }
    // reachable === undefined  → never probed yet, show plain "registered".
    return {
      dotCls: "bg-[hsl(var(--ok))] animate-pulse",
      aria: "ok",
      label: "registered",
    };
  }
  if (reg === "registering") {
    return {
      dotCls: "bg-[hsl(var(--warn))] animate-pulse",
      aria: "probing",
      label: "registering…",
    };
  }
  if (reg === "auth-failed" || reg === "rejected") {
    return {
      dotCls: "bg-[hsl(var(--danger))]",
      aria: "down",
      label: reg,
    };
  }
  if (reg === "timeout") {
    return {
      dotCls: "bg-[hsl(var(--warn))]",
      aria: "down",
      label: "REGISTER timeout",
    };
  }
  // No register_status (IP-auth trunks) — fall back to OPTIONS-only behaviour.
  if (h.reachable === true) {
    return {
      dotCls: "bg-[hsl(var(--ok))] animate-pulse",
      aria: "ok",
      label: `${code ?? "200"}${typeof rtt === "number" ? ` · ${rtt}ms` : ""}`,
    };
  }
  if (h.reachable === false) {
    return {
      dotCls: "bg-[hsl(var(--danger))]",
      aria: "down",
      label: h.last_reason === "timeout" ? "timeout" : (h.last_reason || "down"),
    };
  }
  if (h.total_probes > 0) {
    return {
      dotCls: "bg-[hsl(var(--warn))] animate-pulse",
      aria: "probing",
      label: "probing…",
    };
  }
  return { dotCls: "bg-muted-foreground/40", aria: "unknown", label: "not probed" };
}

/**
 * ReachabilityLED — 10×10 rounded square "LED". Pulses when reachable
 * or probing. Matches the 3CX/Vodia single-glance indicator.
 */
function ReachabilityLED({ state, className = "" }) {
  return (
    <span
      className={`h-2.5 w-2.5 rounded-[2px] shrink-0 ${state.dotCls} ${className}`}
      aria-label={`reachability-${state.aria}`}
      data-testid="trunk-led"
    />
  );
}

/**
 * DirectionGlyph — one arrow (← inbound, → outbound) or two parallel
 * arrows (⇄ bidirectional) — no pill, no text, just a clean glyph
 * like 3CX's trunk matrix. Tooltip explains intent on hover.
 */
function DirectionGlyph({ direction, t }) {
  const dir = (direction || "both").toLowerCase();
  const common = "h-4 w-4 mx-auto";
  if (dir === "inbound") {
    return (
      <ArrowLeft
        className={`${common} text-[hsl(var(--info))]`}
        aria-label="inbound"
        data-testid="dir-inbound"
      >
        <title>{t("pages.trunks.dirTip.inbound")}</title>
      </ArrowLeft>
    );
  }
  if (dir === "outbound") {
    return (
      <ArrowRight
        className={`${common} text-[hsl(var(--warn))]`}
        aria-label="outbound"
        data-testid="dir-outbound"
      >
        <title>{t("pages.trunks.dirTip.outbound")}</title>
      </ArrowRight>
    );
  }
  return (
    <ArrowLeftRight
      className={`${common} text-[hsl(var(--ok))]`}
      aria-label="bidirectional"
      data-testid="dir-both"
    >
      <title>{t("pages.trunks.dirTip.both")}</title>
    </ArrowLeftRight>
  );
}

function ReachabilityCell({ health }) {
  const h = health || {};
  const state = computeReachState(h);
  const ack = h.last_probe_ack_ts || h.last_probe_sent_ts;
  const rel = relTime(ack);
  // Phase 6h — register status pill (separate axis from reachability).
  // Only rendered when the trunk has `register_with_carrier=True`,
  // signalled by the presence of `register_status` in the trunk_health
  // doc.
  const regStatus = h.register_status;
  let regCls = "";
  let regLabel = "";
  let regTitle = "";
  if (regStatus) {
    switch (regStatus) {
      case "registered":
        regCls = "bg-[hsl(var(--ok))]/15 text-[hsl(var(--ok))] border-[hsl(var(--ok))]/40";
        regLabel = "registered";
        regTitle = h.register_granted_seconds
          ? `Granted ${h.register_granted_seconds}s by carrier`
          : "Registered with carrier";
        break;
      case "registering":
        regCls = "bg-[hsl(var(--warn))]/15 text-[hsl(var(--warn))] border-[hsl(var(--warn))]/40";
        regLabel = "registering…";
        regTitle = "REGISTER in flight";
        break;
      case "auth-failed":
        regCls = "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40";
        regLabel = "auth failed";
        regTitle = h.register_last_error || "Carrier rejected digest auth";
        break;
      case "rejected":
        regCls = "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))] border-[hsl(var(--danger))]/40";
        regLabel = "rejected";
        regTitle = h.register_last_error || "Carrier rejected REGISTER";
        break;
      case "timeout":
        regCls = "bg-[hsl(var(--warn))]/15 text-[hsl(var(--warn))] border-[hsl(var(--warn))]/40";
        regLabel = "timeout";
        regTitle = "No REGISTER reply from carrier";
        break;
      case "disabled":
        regCls = "bg-muted/40 text-muted-foreground border-border";
        regLabel = "register off";
        regTitle = "Register-with-carrier toggle is OFF";
        break;
      default:
        regCls = "bg-muted/40 text-muted-foreground border-border";
        regLabel = regStatus;
    }
  }
  return (
    <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="trunk-reachability">
      {/* Phase 6o Hotfix G — LED moved to the Trunk/Name cell; this cell
          now only carries the *details* of the last probe + REG badge,
          so operators can scan the whole row without double-dipping on
          the status indicator. */}
      <span className="font-mono text-[11px]">{state.label}</span>
      {rel && <span className="text-muted-foreground">{rel}</span>}
      {regLabel && (
        <span
          className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${regCls}`}
          title={regTitle}
          data-testid="trunk-register-status"
        >
          REG · {regLabel}
        </span>
      )}
    </div>
  );
}

// Phase 6i (cust) — CustomPresetManager.
//
// Lightweight inline manager for tenant-defined trunk presets. Lives
// inside the trunk editor (collapsed by default — operators only see
// it after clicking "Manage custom presets") so we don't pollute the
// trunks list with extra navigation.
//
// Form mirrors the backend CustomTrunkPresetCreate model:
//   • name                — display label
//   • summary             — one-line description shown under dropdown
//   • fields              — comma-separated list of canonical keys
//   • username_template   — `{key}`-substitution string
//   • defaults / helpers  — optional JSON
//
// On save we POST to /trunk-presets, then call onChange() so the
// parent reloads the list. Uses a JSON textbox for defaults/helpers/
// aliases — power users only; the dropdown remains the primary UX.
const ALLOWED_FIELDS = [
  "account", "subaccount", "username", "password",
  "host", "port", "transport", "auth_mode", "from_user",
];

function CustomPresetManager({ customPresets, onChange }) {
  const [editing, setEditing] = useState(null); // null|{id?,form}
  const startNew = () => setEditing({
    form: {
      name: "",
      summary: "",
      fields: "username,password,host",
      username_template: "{username}",
      defaults: "{}",
      helpers: "{}",
      aliases: "{}",
    },
  });
  const startEdit = (p) => setEditing({
    id: p.id,
    form: {
      name: p.name || "",
      summary: p.summary || "",
      fields: (p.fields || []).join(", "),
      username_template: p.username_template || "{username}",
      defaults: JSON.stringify(p.defaults || {}, null, 2),
      helpers: JSON.stringify(p.helpers || {}, null, 2),
      aliases: JSON.stringify(p.aliases || {}, null, 2),
    },
  });
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!editing) return;
    const f = editing.form;
    let defaults = {};
    let helpers = {};
    let aliases = {};
    try {
      defaults = JSON.parse(f.defaults || "{}");
    } catch {
      toast.error("Defaults must be valid JSON");
      return;
    }
    try {
      helpers = JSON.parse(f.helpers || "{}");
    } catch {
      toast.error("Helpers must be valid JSON");
      return;
    }
    try {
      aliases = JSON.parse(f.aliases || "{}");
    } catch {
      toast.error("Aliases must be valid JSON");
      return;
    }
    const fieldsArr = (f.fields || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    const bad = fieldsArr.filter((x) => !ALLOWED_FIELDS.includes(x));
    if (bad.length) {
      toast.error(`Unknown fields: ${bad.join(", ")}`);
      return;
    }
    const body = {
      name: (f.name || "").trim(),
      summary: f.summary || "",
      fields: fieldsArr,
      username_template: (f.username_template || "{username}").trim(),
      defaults,
      helpers,
      aliases,
    };
    if (!body.name) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing.id) {
        await api.put(`/trunk-presets/${editing.id}`, body);
        toast.success("Preset updated");
      } else {
        await api.post("/trunk-presets", body);
        toast.success("Preset created");
      }
      setEditing(null);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!confirm(`Delete custom preset "${p.name}"?`)) return;
    try {
      await api.delete(`/trunk-presets/${p.id}`);
      toast.success("Preset deleted");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div
      className="rounded-md border border-border bg-background p-3 space-y-3"
      data-testid="trunk-custom-preset-mgr"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h4 className="text-sm font-semibold">Custom presets</h4>
        {!editing && (
          <Button size="sm" variant="outline" onClick={startNew} data-testid="btn-new-custom-preset">
            + New custom preset
          </Button>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={editing.form.name}
                onChange={(e) =>
                  setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })
                }
                placeholder="MyCarrier US"
                data-testid="custom-preset-name"
              />
            </div>
            <div>
              <Label className="text-xs">Username template</Label>
              <Input
                className="font-mono text-xs"
                value={editing.form.username_template}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    form: { ...editing.form, username_template: e.target.value },
                  })
                }
                placeholder="{username} or {provider_account}_{provider_subaccount}"
                data-testid="custom-preset-username-template"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Summary</Label>
            <Input
              value={editing.form.summary}
              onChange={(e) =>
                setEditing({ ...editing, form: { ...editing.form, summary: e.target.value } })
              }
              placeholder="One-line description shown under the dropdown."
            />
          </div>
          <div>
            <Label className="text-xs">Fields (comma-separated)</Label>
            <Input
              className="font-mono text-xs"
              value={editing.form.fields}
              onChange={(e) =>
                setEditing({ ...editing, form: { ...editing.form, fields: e.target.value } })
              }
              placeholder="account, subaccount, password, host"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Allowed: {ALLOWED_FIELDS.join(", ")}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Defaults (JSON)</Label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs font-mono"
                rows={4}
                value={editing.form.defaults}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    form: { ...editing.form, defaults: e.target.value },
                  })
                }
                placeholder='{"host":"sip.example.com","auth_mode":"digest"}'
              />
            </div>
            <div>
              <Label className="text-xs">Helpers (JSON)</Label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs font-mono"
                rows={4}
                value={editing.form.helpers}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    form: { ...editing.form, helpers: e.target.value },
                  })
                }
                placeholder='{"host":"From your portal"}'
              />
            </div>
            <div>
              <Label className="text-xs">Aliases (JSON)</Label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs font-mono"
                rows={4}
                value={editing.form.aliases}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    form: { ...editing.form, aliases: e.target.value },
                  })
                }
                placeholder='{"account":"Trunk ID"}'
              />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button size="sm" onClick={save} data-testid="btn-save-custom-preset">
              {editing.id ? "Update preset" : "Create preset"}
            </Button>
            <Button size="sm" variant="ghost" onClick={cancel}>
              Cancel
            </Button>
          </div>
        </div>
      ) : customPresets.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No custom presets yet. Click <strong>+ New custom preset</strong> to define one for any
          carrier not in the built-in list.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {customPresets.map((p) => (
            <li
              key={p.id}
              className="flex items-baseline justify-between gap-3 rounded border border-border/50 bg-muted/10 px-2 py-1.5 text-xs"
              data-testid={`custom-preset-row-${p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium">{p.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">
                  {p.username_template} · fields: {(p.fields || []).join(", ") || "—"}
                </div>
              </div>
              <button
                type="button"
                className="text-primary underline hover:opacity-80"
                onClick={() => startEdit(p)}
              >
                Edit
              </button>
              <button
                type="button"
                className="text-destructive underline hover:opacity-80"
                onClick={() => remove(p)}
              >
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function TrunksPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  // Phase 6o Hotfix H — `providers` state retired together with the
  // Provider/Reseller tier. Trunks no longer carry a `provider_id`
  // pointer, and the editor has no Provider dropdown, so fetching
  // `/tenants?kind=provider` is pure dead weight.
  const [health, setHealth] = useState({});
  const counters = useCounters(items.length);
  // Phase 6o Hotfix G — tracesParty state removed. Trunk traces moved
  // into the editor's Diagnostics tab; the slide-out Sheet was only
  // needed when traces were a per-row action, which they no longer are.
  // Phase N+ — inline editor state replaces the old <Dialog>.
  //   null          → list view
  //   { id, form }  → inline editor (id === null for new trunk)
  const [editor, setEditor] = useState(null);
  const [probingId, setProbingId] = useState(null);
  // Phase 6f — inline auth-probe result for the open editor.
  // null     -> never tested in this editor session
  // {state}  -> last probe result (ok/stage/status_code/details/...)
  const [authProbe, setAuthProbe] = useState(null);
  const [authProbeRunning, setAuthProbeRunning] = useState(false);
  // Phase 6i (cust) — tenant-defined custom trunk presets.
  // null while loading, [] once fetched.
  const [customPresets, setCustomPresets] = useState([]);
  const [presetMgrOpen, setPresetMgrOpen] = useState(false);

  const loadCustomPresets = async () => {
    try {
      const r = await api.get("/trunk-presets");
      setCustomPresets(Array.isArray(r.data) ? r.data : []);
    } catch {
      setCustomPresets([]);
    }
  };

  const load = async () => {
    // Phase 6o Hotfix H — /tenants?kind=provider retired; the provider
    // tier no longer exists. Trunks belong directly to their org, so
    // there's no provider dropdown to populate in the editor.
    const [trunksRes, healthRes] = await Promise.all([
      api.get("/trunks"),
      api.get("/trunks-health").catch(() => ({ data: {} })),
    ]);
    setItems(trunksRes.data);
    setHealth(healthRes.data || {});
    await loadCustomPresets();
  };
  useEffect(() => {
    load();
    // Auto-refresh reachability every 10s without re-fetching full trunks.
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/trunks-health");
        setHealth(r.data || {});
      } catch { /* ignore transient errors */ }
    }, 10000);
    return () => clearInterval(iv);
  }, []);

  const save = async () => {
    if (!editor) return;
    try {
      const form = editor.form;
      const body = {
        ...form,
        port: Number(form.port || 5060),
        channels: Number(form.channels || 0),
        channels_in: Number(form.channels_in || 0),
        channels_out: Number(form.channels_out || 0),
        channel_policy: form.channel_policy || "total",
        direction: form.direction || "both",
        use_srv: !!form.use_srv,
        register_with_carrier: !!form.register_with_carrier,
        register_expiry_seconds: Math.max(
          30,
          Math.min(3600, parseInt(form.register_expiry_seconds, 10) || 300),
        ),
        codecs: Array.isArray(form.codecs)
          ? form.codecs
          : String(form.codecs).split(",").map((s) => s.trim()).filter(Boolean),
        allowed_ips: Array.isArray(form.allowed_ips)
          ? form.allowed_ips
          : String(form.allowed_ips).split(",").map((s) => s.trim()).filter(Boolean),
        // Phase 6o Hotfix H — provider_id dropped from save payload.
        provider_preset: form.provider_preset || "generic",
        provider_account: (form.provider_account || "").trim(),
        provider_subaccount: (form.provider_subaccount || "").trim(),
      };
      // Phase 6i — when a non-generic preset is in use, the canonical
      // SIP `username` is derived from the preset's `buildUsername` so
      // operators can't accidentally save a half-configured account/sub
      // pair. For voip.ms this concatenates `<account>_<sub>`; for
      // sip.us it copies the account verbatim; for generic it leaves
      // whatever the operator typed.
      const derived = buildUsername(body.provider_preset, body, customPresets);
      if (body.provider_preset && body.provider_preset !== "generic" && derived) {
        body.username = derived;
      }
      if (editor.id) await api.put(`/trunks/${editor.id}`, body);
      else await api.post("/trunks", body);
      toast.success(t("common.saved"));
      setEditor(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.saveFailed"));
    }
  };

  const del = async (tk) => {
    const _ok = await confirm({ title: t("confirm.deleteTrunk", { name: tk.name }), danger: true, confirmLabel: t("common.delete") || "Delete" });
    if (!_ok) return;
    await api.delete(`/trunks/${tk.id}`);
    toast.success(t("common.deleted"));
    load();
  };

  const togglePause = async (tk) => {
    try {
      await api.put(`/trunks/${tk.id}`, { enabled: !tk.enabled });
      toast.success(
        !tk.enabled
          ? t("pages.trunks.resumedToast", { name: tk.name })
          : t("pages.trunks.pausedToast", { name: tk.name })
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };

  const probeTrunk = async (tk) => {
    if (probingId) return;
    setProbingId(tk.id);
    try {
      await api.post(`/trunks/${tk.id}/probe`);
      toast.success(t("pages.trunks.probeQueued", { name: tk.name }));
      const started = Date.now();
      const tick = async () => {
        try {
          const r = await api.get(`/trunks/${tk.id}/health`);
          setHealth((h) => ({ ...h, [tk.id]: r.data }));
        } catch { /* ignore */ }
      };
      const poll = async () => {
        while (Date.now() - started < 9000) {
          await new Promise((r) => setTimeout(r, 1500));
          await tick();
        }
      };
      await poll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally {
      setProbingId(null);
    }
  };

  const edit = (tk) => {
    setAuthProbe(null);
    setEditor({ id: tk.id, form: { ...tk } });
  };
  const newTrunk = () => {
    setAuthProbe(null);
    setEditor({ id: null, form: { ...EMPTY } });
  };

  // Phase 6f — fire a one-shot auth probe against this trunk.
  const runAuthProbe = async () => {
    if (!editor?.id) return;
    setAuthProbeRunning(true);
    setAuthProbe(null);
    try {
      const r = await api.post(`/trunks/${editor.id}/test-auth`);
      setAuthProbe(r.data);
      if (r.data?.ok) {
        toast.success("Trunk auth OK");
      } else {
        toast.error(r.data?.details || "Trunk auth probe failed");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || String(e?.message || e);
      setAuthProbe({ ok: false, stage: "error", details: detail });
      toast.error(detail);
    } finally {
      setAuthProbeRunning(false);
    }
  };

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (editor !== null) {
    const form = editor.form;
    const setForm = (next) =>
      setEditor({ ...editor, form: typeof next === "function" ? next(form) : next });
    return (
      <InlineEditorShell
        crumb={t("pages.trunks.title")}
        title={
          editor.id
            ? `${t("pages.trunks.dialogEdit")}${form.name?.trim() ? `: ${form.name.trim()}` : ""}`
            : t("pages.trunks.dialogNew")
        }
        onCancel={() => setEditor(null)}
        onSave={save}
        saveDisabled={!form.name?.trim() || !form.host?.trim()}
        testidRoot="trunk-editor"
      >
        <div className="space-y-3">
          {/* Phase 6i — Provider preset selector. Picks the carrier
              convention (voip.ms sub-accounts, sip.us account-as-username,
              Telnyx, etc.) and applies sensible defaults. Switching to a
              new preset only fills fields that haven't been touched yet —
              we never overwrite something the operator typed. */}
          <div className="rounded-md border border-border bg-muted/20 p-3 space-y-2">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <Label className="text-sm font-semibold">Carrier preset</Label>
              <div className="flex items-center gap-3 text-[11px]">
                <button
                  type="button"
                  onClick={() => setPresetMgrOpen((v) => !v)}
                  className="underline text-primary hover:opacity-80"
                  data-testid="trunk-presets-manage-toggle"
                >
                  {presetMgrOpen ? "Close manager" : "Manage custom presets"}
                </button>
                <span className="text-muted-foreground">
                  Hides irrelevant fields and pre-fills carrier defaults.
                </span>
              </div>
            </div>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.provider_preset || "generic"}
              data-testid="trunk-provider-preset"
              onChange={(e) => {
                const next = e.target.value;
                const preset = getPreset(next, customPresets);
                const defaults = preset.defaults || {};
                setForm((cur) => {
                  // Only apply defaults for fields the operator hasn't
                  // already customized — protects against overwriting
                  // edits when toggling between presets.
                  const merged = { ...cur, provider_preset: next };
                  for (const [k, v] of Object.entries(defaults)) {
                    const cv = cur[k];
                    if (cv === undefined || cv === null || cv === "" || cv === false) {
                      merged[k] = v;
                    }
                  }
                  return merged;
                });
              }}
            >
              <optgroup label="Built-in">
                {PRESET_LIST.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </optgroup>
              {customPresets.length > 0 && (
                <optgroup label="Your custom presets">
                  {customPresets.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            {(() => {
              const preset = getPreset(form.provider_preset, customPresets);
              return preset.summary ? (
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  {preset.summary}
                </p>
              ) : null;
            })()}
            {presetMgrOpen && (
              <CustomPresetManager
                customPresets={customPresets}
                onChange={loadCustomPresets}
              />
            )}
          </div>
          {/* Phase 6n-d — 6-tab editor mirrors the 3CX VoipMS editor
              (IMG_0282) muscle memory. Carrier preset stays as a banner
              ABOVE the tabs because it influences which fields each
              tab will surface. Tabs preserve DOM order of the original
              flat form so the operator's eye-path doesn't have to
              relearn anything; we just chunk the scroll. */}
          <Tabs defaultValue="general" className="w-full" data-testid="trunk-editor-tabs">
            <TabsList
              className="w-full justify-start gap-1 overflow-x-auto rounded-none border-b bg-card px-2 py-1 h-auto"
            >
              <TabsTrigger value="general" data-testid="trunk-tab-general"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.general", "General")}
              </TabsTrigger>
              <TabsTrigger value="connection" data-testid="trunk-tab-connection"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.connection", "Connection")}
              </TabsTrigger>
              <TabsTrigger value="credentials" data-testid="trunk-tab-credentials"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.credentials", "Credentials")}
              </TabsTrigger>
              <TabsTrigger value="routing" data-testid="trunk-tab-routing"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.routing", "Routing & Failover")}
              </TabsTrigger>
              <TabsTrigger value="policy" data-testid="trunk-tab-policy"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.policy", "Policy")}
              </TabsTrigger>
              <TabsTrigger value="codecs" data-testid="trunk-tab-codecs"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.codecs", "Codecs")}
              </TabsTrigger>
              <TabsTrigger value="diag" data-testid="trunk-tab-diag"
                className="text-xs md:text-sm data-[state=active]:border-b-2 data-[state=active]:border-[hsl(var(--accent))] data-[state=active]:text-foreground rounded-none">
                {t("pages.trunks.tabs.diagnostics", "Diagnostics")}
              </TabsTrigger>
            </TabsList>

            {/* TAB 1 — GENERAL: identity & target endpoint. */}
            <TabsContent value="general" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-endpoint">
                {t("pages.trunks.sections.endpoint", "Endpoint")}
              </SectionHeading>
              <Separator className="my-2" />
          {/* Phase 6o Hotfix H — name now spans the full row; the
              Provider tenant dropdown is retired (no provider tier). */}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <Label>{t("fields.name")}</Label>
              <Input
                data-testid="trunk-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>{t("fields.host")}</Label>
              <Input
                data-testid="trunk-host"
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="sip.provider.net"
              />
            </div>
            <div>
              <Label>{t("fields.port")}</Label>
              <Input
                type="number"
                value={form.port}
                onChange={(e) => setForm({ ...form, port: e.target.value })}
              />
            </div>
          </div>

          {/* Phase 4n — DNS SRV toggle. When on, engine resolves
              `_sip._<transport>.<host>` and balances across returned
              targets. Falls back transparently to A/AAAA if SRV
              times out or NXDOMAINs, so this is always safe to flip. */}
          <div className="rounded-md border border-border p-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-medium">
                {t("pages.trunks.useSrvTitle", "Use DNS SRV lookup")}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                {t(
                  "pages.trunks.useSrvHelp",
                  'Resolve the host via DNS SRV (`_sip._<transport>.<host>`) and weight-balance across returned targets. Ideal for carriers like sip.us that publish `gw.sip.us` as an SRV pool. Falls back to plain A lookup if no SRV record exists.'
                )}
              </p>
            </div>
            <Switch
              checked={!!form.use_srv}
              onCheckedChange={(v) => setForm({ ...form, use_srv: v })}
              data-testid="trunk-use-srv"
            />
          </div>
            </TabsContent>

            {/* TAB 2 — CONNECTION: registration loop, transport, auth-mode pick, channel cap. */}
            <TabsContent value="connection" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-registration">
                {t("pages.trunks.sections.registration", "Registration")}
              </SectionHeading>
              <Separator className="my-2" />

          {/* Phase 6h — Outbound REGISTER toggle. Required by carriers
              that gate INVITE acceptance on a successful registration
              from the calling endpoint (voip.ms / Telnyx /  Flowroute in
              user-password mode). Carriers that use IP authentication
              (sip.us when allowlisted) leave this OFF. */}
          <div className="rounded-md border border-border p-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-xs font-medium">
                Register with carrier
              </div>
              <p className="text-[11px] text-muted-foreground mt-1">
                Periodically send <span className="font-mono">REGISTER</span> to the carrier and
                handle digest challenges. Required by voip.ms, Telnyx, and
                Flowroute in user/password mode. Leave OFF for IP-authenticated
                trunks (e.g. sip.us when your VPS IP is allowlisted).
              </p>
            </div>
            <Switch
              checked={!!form.register_with_carrier}
              onCheckedChange={(v) => setForm({ ...form, register_with_carrier: v })}
              data-testid="trunk-register-with-carrier"
            />
          </div>
          {form.register_with_carrier && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Suggested expiry (seconds)</Label>
                <Input
                  type="number"
                  min={30}
                  max={3600}
                  value={form.register_expiry_seconds ?? 300}
                  onChange={(e) => setForm({ ...form, register_expiry_seconds: e.target.value })}
                  data-testid="trunk-register-expiry"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Carrier may shorten this. Engine refreshes at ~25% of granted expiry. 30–3600s.
                </p>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label>{t("fields.transport")}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.transport}
                onChange={(e) => setForm({ ...form, transport: e.target.value })}
              >
                <option value="udp">UDP</option>
                <option value="tcp">TCP</option>
                <option value="tls">TLS</option>
              </select>
            </div>
            <div>
              <Label>{t("fields.authMode")}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={form.auth_mode}
                onChange={(e) => setForm({ ...form, auth_mode: e.target.value })}
              >
                <option value="ip">IP</option>
                <option value="digest">Digest</option>
              </select>
            </div>
            <div>
              <Label>{t("fields.channels")}</Label>
              <Input
                type="number"
                value={form.channels}
                onChange={(e) => setForm({ ...form, channels: e.target.value })}
              />
            </div>
          </div>
            </TabsContent>

            {/* TAB 3 — CREDENTIALS: digest auth fields + derived SIP user preview + Allowed IPs. */}
            <TabsContent value="credentials" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-credentials">
                {t("pages.trunks.sections.credentials", "Authentication")}
              </SectionHeading>
              <Separator className="my-2" />

          {form.auth_mode === "digest" && (() => {
            const preset = getPreset(form.provider_preset, customPresets);
            const fields = preset.fields || [];
            const aliases = preset.aliasFields || {};
            const helpers = preset.helpers || {};
            const showAccount = fields.includes("account");
            const showSubaccount = fields.includes("subaccount");
            const showUsername = fields.includes("username");
            const computedUsername = buildUsername(preset.id, form, customPresets);
            const presetIsCustom = preset.id === "generic";
            return (
              <div className="space-y-3">
                {showAccount && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>{aliases.account || "Account"}</Label>
                      <Input
                        value={form.provider_account || ""}
                        onChange={(e) =>
                          setForm({ ...form, provider_account: e.target.value })
                        }
                        data-testid="trunk-provider-account"
                      />
                      {helpers.account && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {helpers.account}
                        </p>
                      )}
                    </div>
                    {showSubaccount && (
                      <div>
                        <Label>{aliases.subaccount || "Sub-account"}</Label>
                        <Input
                          value={form.provider_subaccount || ""}
                          onChange={(e) =>
                            setForm({ ...form, provider_subaccount: e.target.value })
                          }
                          data-testid="trunk-provider-subaccount"
                        />
                        {helpers.subaccount && (
                          <p className="text-[11px] text-muted-foreground mt-1">
                            {helpers.subaccount}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {showUsername && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label>{aliases.username || t("fields.username")}</Label>
                      <Input
                        value={form.username || ""}
                        onChange={(e) => setForm({ ...form, username: e.target.value })}
                        data-testid="trunk-username"
                      />
                      {helpers.username && (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {helpers.username}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>{t("fields.password")}</Label>
                    <PasswordInput
                      value={form.password || ""}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    {helpers.password && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        {helpers.password}
                      </p>
                    )}
                  </div>
                </div>
                {!presetIsCustom && (showAccount || showSubaccount) && (
                  /* Read-only "what we'll send on the wire" preview.
                     Critical for debugging carriers like voip.ms where the
                     user types two parts but the digest auth uses a third
                     concatenated string. */
                  <div className="rounded-md border border-dashed border-border/70 bg-muted/20 p-3 text-xs">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="font-mono">
                        <span className="text-muted-foreground">SIP auth username will be:</span>{" "}
                        <span
                          className="text-foreground font-semibold"
                          data-testid="trunk-derived-username"
                        >
                          {computedUsername || "(empty — fill the fields above)"}
                        </span>
                      </div>
                      {computedUsername && (
                        <button
                          type="button"
                          className="text-[11px] underline text-primary hover:opacity-80"
                          onClick={() => {
                            navigator.clipboard
                              ?.writeText(computedUsername)
                              .then(() => toast.success("Copied"))
                              .catch(() => toast.error("Copy failed"));
                          }}
                          data-testid="trunk-copy-derived-username"
                        >
                          Copy
                        </button>
                      )}
                    </div>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      The engine sends this string in the SIP{" "}
                      <span className="font-mono">Authorization</span> header during digest auth.
                    </p>
                  </div>
                )}
              </div>
            );
          })()}
            </TabsContent>

            {/* TAB 4 — ROUTING & FAILOVER: direction filter and per-trunk failover chain. */}
            <TabsContent value="routing" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-routing">
                {t("pages.trunks.sections.routing", "Routing & Failover")}
              </SectionHeading>
              <Separator className="my-2" />

          <div>
            <Label>{t("fields.direction")}</Label>
            <select
              data-testid="trunk-direction"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.direction || "both"}
              onChange={(e) => setForm({ ...form, direction: e.target.value })}
            >
              <option value="both">{t("pages.trunks.direction.both")}</option>
              <option value="inbound">{t("pages.trunks.direction.inbound")}</option>
              <option value="outbound">{t("pages.trunks.direction.outbound")}</option>
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              {t("pages.trunks.directionHelp")}
            </p>
          </div>

          {/* Per-trunk failover chain. */}
          <div className="rounded-md border border-border p-3 space-y-2">
            <div className="text-xs font-medium">
              {t("pages.trunks.failoverTitle")}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {t("pages.trunks.failoverHelp")}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {(form.failover_trunk_ids || []).map((fid, i) => {
                const tk = items.find((x) => x.id === fid);
                return (
                  <span
                    key={fid}
                    className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--accent))]/15 px-2 py-0.5 text-[11px] font-mono"
                    data-testid={`trunk-failover-chip-${i}`}
                  >
                    #{i + 1} {tk ? tk.name : fid}
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-[hsl(var(--danger))]"
                      onClick={() =>
                        setForm({
                          ...form,
                          failover_trunk_ids: form.failover_trunk_ids.filter(
                            (x) => x !== fid
                          ),
                        })
                      }
                    >
                      ×
                    </button>
                  </span>
                );
              })}
              {(form.failover_trunk_ids || []).length === 0 && (
                <span className="text-[11px] italic text-muted-foreground">
                  {t("pages.trunks.failoverEmpty")}
                </span>
              )}
            </div>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value=""
              onChange={(e) => {
                const fid = e.target.value;
                if (!fid) return;
                if ((form.failover_trunk_ids || []).includes(fid)) return;
                if (form.id === fid) return;
                setForm({
                  ...form,
                  failover_trunk_ids: [...(form.failover_trunk_ids || []), fid],
                });
              }}
              data-testid="trunk-failover-add"
            >
              <option value="">{t("pages.trunks.addFailover")}</option>
              {items
                .filter(
                  (tk) =>
                    tk.id !== form.id &&
                    !(form.failover_trunk_ids || []).includes(tk.id) &&
                    ["both", "outbound"].includes((tk.direction || "both").toLowerCase())
                )
                .map((tk) => (
                  <option key={tk.id} value={tk.id}>
                    {tk.name} ({tk.host})
                  </option>
                ))}
            </select>
          </div>
            </TabsContent>

            {/* TAB 5 — POLICY: per-trunk channel policy, in/out splits, allowed-IPs gate. */}
            <TabsContent value="policy" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-policy">
                {t("pages.trunks.sections.policy", "Channel Policy & Access")}
              </SectionHeading>
              <Separator className="my-2" />

          {/* Hotfix Z.11 — channel model simplified to "Total channels"
              only. The in/out split selector and per-direction inputs
              are hidden from the UI; the engine ignores them too. We
              still persist the underlying fields for back-compat with
              existing DB rows. The `channels` (Total) field lives in
              an earlier "Caps" tab so nothing to render here besides
              an explanatory note. */}
          <div className="text-xs text-muted-foreground">
            {t(
              "pages.trunks.policySimpleHint",
              "Channel capacity is controlled by the Total channels field on the Caps tab. Per-direction (In/Out) caps were removed in Z.11 to simplify operations.",
            )}
          </div>
          <div>
            <Label>{t("fields.trunkAllowedIps")}</Label>
            <Input
              value={
                Array.isArray(form.allowed_ips) ? form.allowed_ips.join(", ") : form.allowed_ips
              }
              onChange={(e) => setForm({ ...form, allowed_ips: e.target.value })}
            />
          </div>
            </TabsContent>

            {/* TAB 6 — CODECS: per-trunk media codec policy (Phase 6o Hotfix G:
                promoted to its own tab; was previously the tail of the
                Diagnostics tab, which forced operators to scroll past auth
                probe machinery to edit codecs). */}
            <TabsContent value="codecs" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-codecs">
                {t("pages.trunks.sections.codecs", "Media Codecs")}
              </SectionHeading>
              <Separator className="my-2" />
              <CodecPolicyEditor
                mode={form.codec_policy_mode || "allow"}
                codecs={Array.isArray(form.codecs) ? form.codecs : (form.codecs || "").split(",").map((s) => s.trim()).filter(Boolean)}
                onChange={({ mode, codecs }) =>
                  setForm({ ...form, codec_policy_mode: mode, codecs })
                }
                idPrefix="trunk-codec"
              />
            </TabsContent>

            {/* TAB 7 — DIAGNOSTICS: live auth probe + embedded SIP trace feed
                for this trunk (Phase 6o Hotfix G: traces moved from slide-out
                Sheet into the editor so everything you need to debug a trunk
                lives under one tab). */}
            <TabsContent value="diag" className="p-4 md:p-5 space-y-4">
              <SectionHeading testid="trunk-section-diagnostics">
                {t("pages.trunks.sections.diagnostics", "Diagnostics & Probes")}
              </SectionHeading>
              <Separator className="my-2" />

          {/* Phase 6f — Trunk auth probe. Only meaningful for saved trunks
              (we need the stored creds + host on the server side). */}
          {editor.id && (
            <div className="rounded-md border border-border bg-muted/20 p-3 space-y-3" data-testid="trunk-auth-probe">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">Trunk auth probe</div>
                  <p className="text-xs text-muted-foreground mt-0.5 max-w-prose">
                    Sends a single SIP <span className="font-mono">OPTIONS</span> from this server
                    to the trunk, handles any 401/407 challenge with the saved credentials, and
                    reports back the carrier&apos;s exact response. Non-invasive — no call placed.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={runAuthProbe}
                  disabled={authProbeRunning}
                  data-testid="btn-trunk-test-auth"
                >
                  {authProbeRunning ? "Probing…" : "Test trunk auth"}
                </Button>
              </div>
              {authProbe && (
                <div
                  className={`rounded-md border p-3 text-xs space-y-2 ${
                    authProbe.ok
                      ? "border-[hsl(var(--ok))]/40 bg-[hsl(var(--ok))]/5"
                      : "border-destructive/40 bg-destructive/5"
                  }`}
                  data-testid="trunk-auth-probe-result"
                >
                  <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono">
                    <span>
                      <span className="text-muted-foreground">result:</span>{" "}
                      <strong>{authProbe.ok ? "OK" : "FAIL"}</strong>
                    </span>
                    <span>
                      <span className="text-muted-foreground">stage:</span>{" "}
                      {authProbe.stage || "—"}
                    </span>
                    <span>
                      <span className="text-muted-foreground">status:</span>{" "}
                      {authProbe.status_code || "—"} {authProbe.reason_phrase || ""}
                    </span>
                    {authProbe.target && (
                      <span>
                        <span className="text-muted-foreground">target:</span>{" "}
                        {authProbe.target}
                      </span>
                    )}
                    {typeof authProbe.latency_ms === "number" && authProbe.latency_ms > 0 && (
                      <span>
                        <span className="text-muted-foreground">latency:</span>{" "}
                        {authProbe.latency_ms}ms
                      </span>
                    )}
                    {authProbe.advertised_host && (
                      <span>
                        <span className="text-muted-foreground">advertised host:</span>{" "}
                        {authProbe.advertised_host}
                      </span>
                    )}
                    {authProbe.source_ip && (
                      <span>
                        <span className="text-muted-foreground">source IP:</span>{" "}
                        <span className="text-foreground font-semibold" data-testid="probe-source-ip">
                          {authProbe.source_ip}
                        </span>
                      </span>
                    )}
                  </div>
                  {authProbe.details && (
                    <div className="leading-relaxed">{authProbe.details}</div>
                  )}
                  {authProbe.challenge_raw && (
                    <details className="text-[11px]">
                      <summary className="cursor-pointer text-muted-foreground">
                        Carrier challenge header (raw)
                      </summary>
                      <pre className="mt-1 whitespace-pre-wrap break-all font-mono">
                        {authProbe.challenge_raw}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Phase 6o Hotfix G — embedded SIP trace feed for this trunk.
              Only polls when the Diagnostics tab is mounted (TabsContent
              unmounts inactive tabs) so switching to another tab stops
              the 4s interval. */}
          <div className="pt-2">
            <SectionHeading testid="trunk-section-traces">
              {t("pages.trunks.sections.traces", "Recent SIP traces")}
            </SectionHeading>
            <p className="text-xs text-muted-foreground -mt-1 mb-3">
              {t(
                "pages.trunks.tracesHint",
                "Live engine-level SIP events for this trunk (REGISTER, OPTIONS, INVITE, auth challenges). Updated every few seconds."
              )}
            </p>
            <TracesPane
              party={editor.id ? { id: editor.id, name: form.name, kind: "trunk" } : null}
              dense
              active
            />
          </div>
            </TabsContent>
          </Tabs>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-trunks">
      <PageHeader
        title={t("pages.trunks.title")}
        subtitle={t("pages.trunks.subtitle")}
        icon={Cable}
        actions={
          <Button onClick={newTrunk} data-testid="new-trunk-btn">
            <Plus className="h-4 w-4 mr-1" /> {t("pages.trunks.newBtn")}
          </Button>
        }
      />
      {items.length === 0 ? (
        <EmptyState
          title={t("pages.trunks.emptyTitle")}
          description={t("pages.trunks.emptyDesc")}
          action={
            <Button onClick={newTrunk}>
              <Plus className="h-4 w-4 mr-1" /> {t("pages.trunks.emptyAction")}
            </Button>
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                {/* Phase 6o Hotfix G — compacted from 9 to 6 columns.
                    Provider column retired (was always "Sipus" style
                    noise that added width; will be fully removed when
                    the 3-tier hierarchy is retired in Hotfix H).
                    Auth column folded into the Trunk cell's sub-line.
                    Host column merged into Trunk cell (top = name,
                    bottom = host:port/transport · auth-mode). */}
                <TableHead className="min-w-[260px]">{t("fields.name")}</TableHead>
                <TableHead className="min-w-[200px]">{t("pages.trunks.colReachability")}</TableHead>
                <TableHead className="w-[60px] text-center">{t("pages.trunks.colDirection")}</TableHead>
                <TableHead className="w-[220px]">{t("fields.channels")}</TableHead>
                <TableHead className="w-[160px]">{t("common.status")}</TableHead>
                <TableHead className="w-[110px] text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((tk) => {
                const reachState = computeReachState(health[tk.id]);
                return (
                <TableRow
                  key={tk.id}
                  className="row-hover cursor-pointer"
                  data-testid={`trunk-row-${tk.id}`}
                  onClick={(e) => {
                    if (e.target.closest("button")) return;
                    if (e.target.closest('[role="switch"]')) return;
                    edit(tk);
                  }}
                >
                  <TableCell className="align-middle">
                    {/* Phase 6o Hotfix G — stacked cell: LED + name on top,
                        host:port/transport + auth-mode on bottom. Mirrors
                        Vodia's trunk list where the host is sub-text of
                        the trunk identity, not its own column. */}
                    <div className="flex items-center gap-2.5 min-w-0">
                      <ReachabilityLED state={reachState} />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium truncate">{tk.name}</span>
                          <PolicyPill entity={tk} compact />
                        </div>
                        <div className="font-mono text-[11px] text-muted-foreground truncate">
                          {tk.host}:{tk.port}/{(tk.transport || "udp").toUpperCase()}
                          <span className="mx-1.5 opacity-50">·</span>
                          <span
                            className="uppercase tracking-wider text-[10px]"
                            title={
                              tk.auth_mode === "ip"
                                ? "IP ACL authentication"
                                : "Digest (username/password) authentication"
                            }
                          >
                            {tk.auth_mode === "ip" ? "IP" : "DIGEST"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <ReachabilityCell health={health[tk.id]} />
                  </TableCell>
                  <TableCell className="text-center">
                    <DirectionGlyph direction={tk.direction} t={t} />
                  </TableCell>
                  <TableCell>
                    <ChannelMeter {...meterPropsFromTrunk(tk, counters)} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2" data-testid={`trunk-pause-${tk.id}`}>
                      <Switch
                        checked={!!tk.enabled}
                        onCheckedChange={() => togglePause(tk)}
                        aria-label={
                          tk.enabled ? t("pages.trunks.pauseLabel") : t("pages.trunks.resumeLabel")
                        }
                      />
                      <StatusPill kind={tk.enabled ? "ok" : "offline"}>
                        {tk.enabled ? t("common.enabled") : t("common.paused")}
                      </StatusPill>
                    </div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    {/* Phase 6o Hotfix G — traces button retired. Traces
                        now live inside the trunk editor's Diagnostics
                        tab where an operator already has the full
                        context (probe result, health, codec policy)
                        in one place. */}
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t("pages.trunks.probeTitle")}
                      onClick={() => probeTrunk(tk)}
                      disabled={probingId === tk.id || !tk.enabled}
                      data-testid={`trunk-probe-${tk.id}`}
                    >
                      <Radio
                        className={`h-4 w-4 ${
                          probingId === tk.id ? "animate-pulse text-[hsl(var(--accent))]" : ""
                        }`}
                      />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => edit(tk)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-[hsl(var(--danger))]"
                      onClick={() => del(tk)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );})}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
